import React, { useState, useEffect, useMemo } from 'react';
import { 
  FiSearch, 
  FiEye, 
  FiEdit2, 
  FiTrash2, 
  FiMail,
  FiPhone
} from 'react-icons/fi';
import AddCustomerModal from '../components/Modals/AddCustomerModal';
import ViewCustomerModal from '../components/Modals/ViewCustomerModal';
import ConfirmModal from '../components/Modals/ConfirmModal';
import { authFetch } from '../utils/api';
import { apiCall } from '../utils/apiHelpers';
import { useTranslation } from 'react-i18next';

const CustomersPage = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, customerId: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiCall(authFetch, '/customers');
        setCustomers(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching customers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleViewCustomer = (customer) => {
    setViewingCustomer(customer);
    setIsViewModalOpen(true);
  };

  const handleDeleteCustomer = (customerId) => {
    setConfirmDelete({ open: true, customerId });
  };

  const confirmDeleteCustomer = async () => {
    const customerId = confirmDelete.customerId;
    setConfirmDelete({ open: false, customerId: null });
      try {
        await apiCall(authFetch, `/customers/${customerId}`, { method: 'DELETE' });
        setCustomers(customers.filter(customer => customer.customer_id !== customerId));
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert('Failed to delete customer. Please try again.');
    }
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      if (editingCustomer) {
        // Update
        const updated = await apiCall(authFetch, `/customers/${editingCustomer.customer_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customerData)
        });
        setCustomers(customers.map(c => c.customer_id === editingCustomer.customer_id ? updated : c));
      } else {
        // Create
        const newCustomer = await apiCall(authFetch, '/customers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customerData)
        });
        setCustomers([...customers, newCustomer]);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Failed to save customer. Please try again.');
    }
  };

  // Compute filtered customers in real-time
  const filteredCustomers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const normalizedStatus = (statusFilter || 'all').toLowerCase();

    return customers.filter((customer) => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = (customer.phone || '').toLowerCase();
      const status = (customer.status || '').toLowerCase();

      const matchesQuery =
        normalizedQuery.length === 0 ||
        name.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        String(customer.customer_id || '').toLowerCase().includes(normalizedQuery);

      const matchesStatus =
        normalizedStatus === 'all' || status === normalizedStatus;

      return matchesQuery && matchesStatus;
    });
  }, [customers, searchQuery, statusFilter]);

  return (
    <main className="page-container">
      <div className="page-header">
        <h1>{t('customers.title', 'Customers')}</h1>
        <button className="btn btn-primary" onClick={handleAddCustomer}>{t('customers.addCustomer', '+ Add Customer')}</button>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('customers.loading', 'Loading customers...')}</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p className="error-message">{t('customers.error', 'Error: ')} {error}</p>
          <button className="btn btn-secondary" onClick={() => window.location.reload()}>
            {t('customers.retry', 'Retry')}
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="filters-container">
            <div className="search-container">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={t('customers.searchPlaceholder', 'Search customers...')}
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="status-filter-container">
              <select 
                className="status-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('customers.allStatus', 'All Status')}</option>
                <option value="active">{t('customers.active', 'Active')}</option>
                <option value="inactive">{t('customers.inactive', 'Inactive')}</option>
              </select>
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" />
                  </th>
                  <th>{t('customers.columns.customerId', 'Customer ID')}</th>
                  <th>{t('customers.columns.name', 'Name')}</th>
                  <th>{t('customers.columns.state', 'State')}</th>
                  <th>{t('customers.columns.contact', 'Contact')}</th>
                  <th>{t('customers.columns.totalSpent', 'Total Spent')}</th>
                  <th>{t('customers.columns.lastInvoice', 'Last Invoice')}</th>
                  <th>{t('customers.columns.status', 'Status')}</th>
                  <th>{t('customers.columns.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.customer_id}>
                    <td>
                      <input type="checkbox" />
                    </td>
                    <td>{customer.customer_id}</td>
                    <td>{customer.name}</td>
                    <td>
                      {customer.state ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          background: '#e3f2fd',
                          color: '#1565c0',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {customer.state}
                        </span>
                      ) : (
                        <span style={{ color: '#9e9e9e', fontSize: '0.8rem', fontStyle: 'italic' }}>
                          Not specified
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="contact-info">
                        <span>
                          <FiMail /> {customer.email}
                        </span>
                        <span>
                          <FiPhone /> {customer.phone}
                        </span>
                      </div>
                    </td>
                    <td>{Number(customer.total_spent) > 0 ? `₹${Number(customer.total_spent).toFixed(2)}` : '₹0.00'}</td>
                    <td>{customer.last_invoice ? new Date(customer.last_invoice).toLocaleDateString() : '-'}</td>
                    <td>
                      <span className={`status-badge ${customer.status ? customer.status.toLowerCase() : ''}`}>
                        {customer.status ? t(`customers.${customer.status.toLowerCase()}`, customer.status) : t('customers.unknown', 'Unknown')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button" 
                          title="View"
                          onClick={() => handleViewCustomer(customer)}
                        >
                          <FiEye />
                        </button>
                        <button 
                          className="action-button" 
                          title="Edit"
                          onClick={() => handleEditCustomer(customer)}
                        >
                          <FiEdit2 />
                        </button>
                        <button 
                          className="action-button" 
                          title="Delete"
                          onClick={() => handleDeleteCustomer(customer.customer_id)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AddCustomerModal 
          show={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onSave={handleSaveCustomer}
          editingCustomer={editingCustomer}
        />

      <ViewCustomerModal
        show={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        customer={viewingCustomer}
        onEdit={handleEditCustomer}
      />

      <ConfirmModal
        show={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, customerId: null })}
        onConfirm={confirmDeleteCustomer}
        title={t('customers.deleteTitle', 'Delete Customer?')}
        message={t('customers.deleteMessage', 'Are you sure you want to delete this customer? This action cannot be undone.')}
        confirmText={t('customers.delete', 'Delete')}
        cancelText={t('customers.cancel', 'Cancel')}
        confirmColor="btn-danger"
      />
    </main>
  );
};

export default CustomersPage; 